import { ChangeDetectorRef, EventEmitter, Input, OnChanges, OnInit, Optional, Output, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { App } from '@df/core/app';
import { isSameDay } from '@df/util/fn/is-same-day';
import { toDate } from '@df/util/fn/to-date';
import defaults from 'lodash-es/defaults';
import groupBy from 'lodash-es/groupBy';
import head from 'lodash-es/head';
import isNumber from 'lodash-es/isNumber';
import last from 'lodash-es/last';
import range from 'lodash-es/range';
import values from 'lodash-es/values';
import { FormFieldAbstract } from '@df/ui/form/form-field.abstract';

/**
 * Component options interface
 */
export interface ICalendarOptions {
    firstDayOfWeek?: number; // 0 for saturday, 1 for monday
    min?: Date | string | number;
    max?: Date | string | number;
    validate?: (day: Date) => boolean;
    displayOtherMonthsDates?: boolean;
}

export interface ICalendarDate extends Date {
    isPicked?: boolean;
    isDisabled?: boolean;
    isToday?: boolean;
    otherMonth?: boolean;
}

export interface ICalendarValidateDay {
    day: ICalendarDate;
    valid: boolean;
}

import { Directive } from '@angular/core';

/**
 * @deprecated Use Calendar2ComponentDef
 */
@Directive()
export abstract class CalendarComponentDef extends FormFieldAbstract implements OnInit, OnChanges, ControlValueAccessor {
    /** @internal initialized status */
    private _initiated = false;
    private _now = new Date();

    /**
     * Component options
     * @type {IOptions}
     */
    @Input()
    options: ICalendarOptions = {};

    /**
     * Model change output to support two way binding
     */
    @Output()
    valueChange = new EventEmitter<ICalendarDate | undefined>();

    @Output()
    validChange = new EventEmitter<boolean>();

    @Output()
    changeDate = new EventEmitter<ICalendarDate | undefined>();

    @Output()
    validateDay = new EventEmitter<ICalendarValidateDay>();

    /**
     * All days in month (same as in weeks but not grouped)
     * @type {Date[]}
     */
    protected days!: ICalendarDate[];

    /**
     * Dates (this.days) grouped in weeks
     */
    weeks!: (ICalendarDate | null)[][];
    /**
     * Year for input field
     */
    protected _year!: number;
    /**
     * Sample days to present week days row
     * @type {ICalendarDate[]}
     */
    weekDays!: ICalendarDate[];
    /**
     * Currently displayed month representation date
     * (we dont care about day, but month and year are for currently viewed thing)
     * @type {ICalendarDate}
     */
    monthDate!: ICalendarDate;

    /**
     * If data is valid
     * passed by 2 way binding
     */
    protected _isValid?: boolean;

    /**
     * Component default options
     * @type {IOptions}
     */
    protected defaultOptions: ICalendarOptions = {
        firstDayOfWeek: 0
    };

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        @Optional() @Self() ngControl: NgControl,
        app: App
    ) {
        super(app, ngControl);
        this._setInitialData();
    }

    ngOnInit() {
        this.sanitizeOptions();
        this.initialiseWeekDays();
        this._initiated = true;
        this.update();
    }

    ngOnChanges(changes) {
        if (this._initiated && changes.options) {
            this.sanitizeOptions();
            this.initialiseWeekDays();
        }
        if (this._initiated) {
            this.update();
        }
    }

    protected override setValue(value: ICalendarDate | undefined) {
        if (value !== this._value) {
            if (!(value instanceof Date)) {
                value = value ? toDate(value, true) : undefined;
            }
            this._value = value;
            this.onValueChange(value);
        }
    }

    onValueChange(value: ICalendarDate | undefined) {
        this.onChange(value);

        this.check();

        this.valueChange.emit(value);

        this.updatePicked();
    }

    /**
     * Shifts calendar one month ahead
     */
    nextMonth() {
        this.monthDate = new Date(this.monthDate.setMonth(this.monthDate.getMonth() + 1));
        this.update();
    }

    /**
     * Shifts calendar one month back
     */
    prevMonth() {
        this.monthDate = new Date(this.monthDate.setMonth(this.monthDate.getMonth() - 1));
        this.update();
    }
    /**
     * Shifts calendar one year ahead
     */
    nextYear(): void {
        this.year = this.monthDate.getFullYear() + 1;
    }

    /**
     * Shifts calendar one year back
     */
    prevYear(): void {
        this.year = this.monthDate.getFullYear() - 1;
    }

    set isValid(value: boolean) {
        this._isValid = value;
        this.validChange.emit(this.isValid);
    }
    get isValid(): boolean {
        return this._isValid || false;
    }

    /**
     * Year setter
     */
    set year(year: number) {
        this._year = year;

        if (!isNumber(year)) {
            return;
        }

        this.monthDate = new Date(this.monthDate.setFullYear(this.year));

        if (this.options) {
            this.update();
        }
    }

    /**
     * Year getter
     */
    get year(): number {
        return this._year;
    }

    /**
     * Picks date and binds it to this.model
     * @param {Date} day
     */
    pick(day?: ICalendarDate): void {
        if (day && !day['isDisabled']) {
            this.value = day;
            this.changeDate.emit(this.value);
        }
    }

    trackFn(index: number) {
        return index;
    }

    protected check() {
        this.isValid = !!this.value && this.options && !this.isDisabled(this.value);
    }

    /**
     * Checks if given date is currently picked one
     * @param {Date} day
     */
    protected isPicked(day: ICalendarDate): boolean {
        // we check yearh month and day cause there may be difference in time
        return isSameDay(day, this.value);
    }

    /**
     * Checks if given date is today date
     * @param {Date} day
     */
    protected isToday(day: ICalendarDate): boolean {
        // we check yearh month and day cause there may be difference in time
        return isSameDay(day, this._now);
    }

    /**
     * Updates isPicked flag in dates
     */
    protected updatePicked() {
        (this.weeks || []).forEach(week => {
            week.forEach(date => {
                if (date) {
                    date['isPicked'] = this.isPicked(date);
                }
            });
        });

        this.detectChanges();
    }

    /**
     * Checks if given date is currently picked one
     * @param {Date} day
     */
    protected isDisabled(day: Date): boolean {
        if (!day) {
            return false;
        }

        let valid = true;

        // check range
        if ((this.options.min && this.options.min > day) || (this.options.max && this.options.max < day)) {
            valid = false;
        }
        if (valid && this.options.validate && !this.options.validate(day)) {
            valid = false;
        }

        // call validation callback
        this.validateDay.emit({ day, valid });

        return !valid;
    }

    /**
     * Updates isDisabled flag in dates
     */
    protected updateDisabled() {
        (this.weeks || []).forEach(week => {
            week.forEach(date => {
                if (date) {
                    date['isDisabled'] = this.isDisabled(date);
                    date['isToday'] = this.isToday(date);
                }
            });
        });
    }

    /**
     * Updates this.weekDays - array of Date objects of which first is
     * proper day of a week following firstDayOfWeek setting
     *
     * You use it to show header line with day of week names
     * (use date filter: | date:'EEE' to extract day of week)
     *
     * We keep it as date for better display flexibility
     */
    private initialiseWeekDays(): void {
        const firstWeekDate = new Date();
        const firstDayOfWeek = this.options.firstDayOfWeek || 0;
        while (firstWeekDate.getDay() !== firstDayOfWeek) {
            firstWeekDate.setDate(firstWeekDate.getDate() + 1);
        }

        this.weekDays = range(7).map(i => {
            const weekDay = new Date(firstWeekDate.getTime());
            weekDay.setDate(weekDay.getDate() + i);
            return weekDay;
        });
    }

    /**
     * Takes all days for given month
     * @param {number} month Month 0-11
     * @param {number} year  Year
     * @return {Date[]} Dates array (ordered)
     */
    protected getDaysInMonth(month: number, year: number): Date[] {
        const date = new Date(year, month, 1);
        const days: Date[] = [];
        while (date.getMonth() === month) {
            days.push(new Date(date.getTime()));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }

    /**
     * Takes all days from selected month
     * and groups them into weeks
     *
     * Updates this.weeks property
     */
    protected update() {
        if (!this.monthDate) {
            this._setInitialData();
        }

        this.days = this.getDaysInMonth(this.monthDate.getMonth(), this.monthDate.getFullYear());

        this.weeks = this.getWeeks();

        this.updatePicked();
        this.updateDisabled();
        this.check();

        this.detectChanges();
    }

    protected getWeeks(): (ICalendarDate | null)[][] {
        const firstDayOfMonthDayOfWeek = (<Date>head(this.days)).getDay();
        const daysShift = firstDayOfMonthDayOfWeek - (this.options.firstDayOfWeek || 0);
        const weeks: (Date | null)[][] = values(groupBy(this.days, day => 10 + Math.floor((day.getDate() - 1 + daysShift) / 7)));
        // -1 cause first day is 1st not 0; adding 10 to avoid negative numbers to keep order

        this.addMissingDates(head(weeks), 'first');

        this.addMissingDates(last(weeks), 'last');
        return weeks;
    }

    protected addMissingDates(week: (Date | null)[] | undefined, type: 'first' | 'last'): void {
        if (week) {
            const missingDays = 7 - week.length;
            if (missingDays) {
                const edgeDate = type === 'first' ? head(week) : last(week);
                if (edgeDate) {
                    for (let index = 0; index < missingDays; index++) {
                        let value: Date | null = null;

                        if (this.options?.displayOtherMonthsDates) {
                            const time = edgeDate.getTime();
                            value = new Date(time + (type === 'first' ? -1 : 1) * ((index + 1) * 86400000));
                            value['otherMonth'] = true;
                        }
                        if (type === 'first') {
                            week.unshift(value);
                        } else {
                            week.push(value);
                        }
                    }
                }
            }
        }
    }

    protected detectChanges() {
        if (this._initiated) {
            this.changeDetectorRef.detectChanges();
        }
    }

    private _setInitialData() {
        this.monthDate = new Date();
        this.monthDate = new Date(this.monthDate.setDate(1)); // force first to make sure it exist in any month
        this.year = this.monthDate.getFullYear();
    }

    private sanitizeOptions() {
        // prepare default options
        this.options = defaults(this.options || {}, this.defaultOptions);

        // make sure first day is in 0-6 range
        this.options.firstDayOfWeek = Math.max(Math.min(this.options.firstDayOfWeek || 0, 6), 0);

        // normalize min/max
        this.options.min = toDate(this.options.min);
        this.options.max = toDate(this.options.max);
    }
}
